import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import { addMembersKeys, ROUTE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import AddMember from './add-member-manually';
import AddCsvFile from './add-csv';
import { ReactComponent as FileIcon } from 'assets/icons/file-icon.svg';
import { ActiveType, DepartmentType, MemberType } from './types';
import { defaultData } from './util';
import { getJsDateFromExcel } from 'excel-date-to-js';
import { completeAssignAdminStep, handleApiError } from 'utils/functions';

const AddMembers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setHideSidebar, setBackLinkTitle, setIsBackLink, activeCompany } = useDashboardContext();
  const companyId = activeCompany?.id;
  const [file, setFile] = useState<File | null>(null);
  const [active, setActive] = useState<ActiveType | null>(null);
  const [departmentOptions, setDepartmentOptions] = useState<DepartmentType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [members, setMembers] = useState<MemberType[]>([]);

  const readFile = () => {
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const fileDetails = workbook.SheetNames[0];
        let convertMemberDetails = XLSX.utils.sheet_to_json(workbook.Sheets[fileDetails]);
        convertMemberDetails = convertMemberDetails.map((member: any) => {
          const currMember = member;
          const dob =
            typeof currMember.dob === 'number' ? getJsDateFromExcel(currMember.dob) : undefined;
          const department_name = currMember.department;
          delete currMember.dob;
          delete currMember.department;
          delete currMember['s/n'];
          return {
            ...currMember,
            dob,
            department_name,
            phone_number: String(currMember.phone_number),
            budget: String(currMember.budget),
            role: currMember.role.toUpperCase()
          };
        });
        const members = convertMemberDetails;
        const newDepartments = members.reduce((departments: DepartmentType[], member: any) => {
          if (
            member.department_name &&
            !departmentOptions.find((d) => d.name === member.department_name) &&
            !departments.find((d) => d.name === member.department_name)
          )
            departments.push({ id: member.department_name, name: member.department_name });
          return departments;
        }, []);
        if (newDepartments.length) setDepartmentOptions(departmentOptions.concat(newDepartments));
        setMembers(members as MemberType[]);
      };
    }
  };
  const filterEmptyFields = (data: MemberType): Partial<MemberType> => {
    const filteredData: Partial<MemberType> = {};

    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && (typeof value !== 'string' || value.trim() !== '')) {
        filteredData[key as keyof MemberType] = value as string | undefined;
      }
    }

    return filteredData;
  };

  const onConfirm = async () => {
    if (!companyId) return;
    const data = {
      company_id: companyId,
      members_data: members.map((member) => filterEmptyFields(member))
    };

    try {
      setLoading(true);
      const res = await apiService.post('member/bulk', { data });
      if (res.data) {
        toast(<ToastMessage type='success' message={'Members have been added'} />);
        if (searchParams.get('from') === ROUTE_KEYS.GET_STARTED) {
          completeAssignAdminStep(companyId);
          navigate(`/${ROUTE_KEYS.GET_STARTED}`);
        } else {
          navigate(`${ROUTE_KEYS.APP}/${ROUTE_KEYS.PEOPLE}`);
        }
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHideSidebar(true);
    setIsBackLink(true);
    setBackLinkTitle('Return to Members');
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  useEffect(() => {
    getDepartments();
  }, [companyId]);
  const getDepartments = async () => {
    if (!companyId) return;
    try {
      const res = await apiService.get<{
        data: { name: string }[];
      }>(`department/company/${companyId}`);
      const departments = res.data?.data?.map((d) => ({ id: d.name, name: d.name }));
      setDepartmentOptions(departments);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    readFile();
  }, [file]);

  function hasEmptyField(data: MemberType[]): boolean {
    for (const item of data) {
      if (!item.department_name) return true;
      for (const key in item) {
        if (key === addMembersKeys.dob) return false;
        if (
          typeof item[key as keyof MemberType] === 'string' &&
          item[key as keyof MemberType] === ''
        ) {
          return true;
        }
      }
    }
    return false;
  }

  return (
    <div className='flex flex-col max-w-[390px] w-full mx-auto'>
      <h2 className='text-2xl font-bold'>Add member</h2>
      <p className='text-[#6F7376] text-sm mt-2'>
        Ensure the details provided here is the same as that used to register/signin to the
        FoodCourt App
      </p>

      <div className='flex gap-4 my-6 w-full flex-wrap sm:flex-nowrap'>
        <AddCsvFile
          description='Add multiple guests'
          setMembers={setMembers}
          setActive={setActive}
          setFile={setFile}
          file={file}
        />
        <button
          disabled={active === 'manual'}
          onClick={() => {
            setActive('manual');
            setMembers([defaultData]);
          }}
          className={`border ${
            active === 'manual'
              ? ' cursor-not-allowed  border-[#E60A2B]'
              : 'border-[#EAEBEB] hover:border-[#E60A2B]'
          } w-full sm:w-[185px] hover:border-[1.5px] p-4 rounded-md cursor-pointer space-y-3 flex flex-col`}>
          <div className='border border-[#EAEBEB] p-2 rounded-full  w-9 h-9 flex items-center'>
            <FileIcon />
          </div>
          <h2 className='text-sm font-semibold'>Add manually</h2>
          <p className='text-[#6F7376] text-xs font-normal text-start '>Add multiple guests</p>
        </button>
      </div>

      {active && (
        <AddMember
          members={members}
          setMembers={setMembers}
          departmentOptions={departmentOptions}
          active={active}
        />
      )}
      <Button
        onClick={onConfirm}
        loading={loading}
        disabled={members.length === 0 || hasEmptyField(members)}
        small
        variant='block'
        color='primary'
        className=' mt-8 text-sm'>
        Proceed
      </Button>
    </div>
  );
};

export default AddMembers;
